import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/navigation/AdminHeader';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import ProtectedRoute from '../../components/navigation/ProtectedRoute';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterSidebar from './components/FilterSidebar';
import YardsDataGrid from './components/YardsDataGrid';
import DetailPanel from './components/DetailPanel';
import BulkOperationsToolbar from './components/BulkOperationsToolbar';
import AddYardModal from './components/AddYardModal';
import { supabase } from '../../lib/supabase';

const YardsManagementInterface = () => {
  const navigate = useNavigate();
  const [user] = useState({ email: 'admin@rpmdiesel.com' });
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const [selectedYardId, setSelectedYardId] = useState(null);
  const [selectedYards, setSelectedYards] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingYard, setEditingYard] = useState(null);
  const [sortConfig, setSortConfig] = useState({ column: 'name', direction: 'asc' });
  const [yards, setYards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalSaveError, setModalSaveError] = useState(null);

  const [filters, setFilters] = useState({
    preset: 'all',
    search: '',
    region: 'all',
    status: ['active', 'inactive', 'maintenance'],
    utilization: 'all',
    capacityMin: '',
    capacityMax: ''
  });

  // Map a Supabase row to the shape the UI expects
  const mapRow = (row) => {
    const addressParts = [row?.address1, row?.address2, row?.city, row?.state, row?.zip]?.filter(Boolean);
    return {
      id: row?.id,
      name: row?.name || '',
      address: addressParts?.join(', '),
      address1: row?.address1 || '',
      address2: row?.address2 || '',
      city: row?.city || '',
      state: row?.state || '',
      zip: row?.zip || '',
      region: row?.region || '',
      capacity: row?.capacity ?? null,
      utilization: row?.utilization_percent ?? null,
      manager: row?.manager_name || '',
      status: row?.is_active ? 'active' : 'inactive',
      is_active: row?.is_active,
      created_at: row?.created_at
    };
  };

  const fetchYards = async () => {
    setLoading(true);
    const { data, error } = await supabase?.from('yards')?.select('id, name, address1, address2, city, state, zip, is_active, created_at, region, capacity, manager_name, utilization_percent')?.order('name', { ascending: true });

    if (!error && data) {
      setYards(data?.map(mapRow));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchYards();
  }, []);

  const filteredYards = yards?.filter(yard => {
    if (filters?.search &&
      !yard?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
      !yard?.address?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
      return false;
    }

    if (filters?.region !== 'all' && (yard?.region || '')?.toLowerCase() !== filters?.region?.toLowerCase()) {
      return false;
    }

    if (!filters?.status?.includes(yard?.status)) {
      return false;
    }

    if (filters?.utilization !== 'all') {
      const u = yard?.utilization ?? 0;
      if (filters?.utilization === 'low' && u >= 50) return false;
      if (filters?.utilization === 'medium' && (u < 50 || u > 80)) return false;
      if (filters?.utilization === 'high' && u <= 80) return false;
    }

    if (filters?.capacityMin && (yard?.capacity ?? 0) < parseInt(filters?.capacityMin)) {
      return false;
    }

    if (filters?.capacityMax && (yard?.capacity ?? 0) > parseInt(filters?.capacityMax)) {
      return false;
    }

    return true;
  });

  const sortedYards = [...filteredYards]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.column] ?? '';
    const bValue = b?.[sortConfig?.column] ?? '';

    if (typeof aValue === 'string') {
      return sortConfig?.direction === 'asc'
        ? aValue?.localeCompare(bValue)
        : bValue?.localeCompare(aValue);
    }

    return sortConfig?.direction === 'asc'
      ? (aValue ?? 0) - (bValue ?? 0)
      : (bValue ?? 0) - (aValue ?? 0);
  });

  const selectedYard = yards?.find(yard => yard?.id === selectedYardId);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('sessionExpiry');
    navigate('/admin-authentication-portal');
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleResetFilters = () => {
    setFilters({
      preset: 'all',
      search: '',
      region: 'all',
      status: ['active', 'inactive', 'maintenance'],
      utilization: 'all',
      capacityMin: '',
      capacityMax: ''
    });
  };

  const handleSelectYard = (yardId, checked) => {
    if (checked) {
      setSelectedYards([...selectedYards, yardId]);
    } else {
      setSelectedYards(selectedYards?.filter(id => id !== yardId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedYards(filteredYards?.map(yard => yard?.id));
    } else {
      setSelectedYards([]);
    }
  };

  const handleSort = (column) => {
    setSortConfig({
      column,
      direction: sortConfig?.column === column && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  // Called by AddYardModal for both add and edit
  const handleSaveYard = async (formData) => {
    setModalSaveError(null);

    const payload = {
      name: formData?.name,
      address1: formData?.address1 || null,
      address2: formData?.address2 || null,
      city: formData?.city || null,
      state: formData?.state || null,
      zip: formData?.zip || null,
      is_active: formData?.status !== 'inactive',
      region: formData?.region || null,
      capacity: formData?.capacity ? parseInt(formData?.capacity) : null,
      manager_name: formData?.manager || null,
      utilization_percent: formData?.utilization_percent ? parseInt(formData?.utilization_percent) : null
    };

    let error;
    if (editingYard) {
      const result = await supabase?.from('yards')?.update(payload)?.eq('id', editingYard?.id);
      error = result?.error;
    } else {
      const result = await supabase?.from('yards')?.insert([payload]);
      error = result?.error;
    }

    if (error) {
      setModalSaveError(`Failed to ${editingYard ? 'update' : 'create'} yard: ${error?.message}`);
      return; // keep modal open
    }

    // Success — close modal and refresh
    setIsAddModalOpen(false);
    setEditingYard(null);
    setModalSaveError(null);
    await fetchYards();
  };

  const handleOpenAddModal = () => {
    setEditingYard(null);
    setModalSaveError(null);
    setIsAddModalOpen(true);
  };

  const handleOpenEditModal = (yard) => {
    setEditingYard(yard);
    setModalSaveError(null);
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditingYard(null);
    setModalSaveError(null);
  };

  const handleDeleteYard = async (yardId) => {
    if (window.confirm('Are you sure you want to delete this yard?')) {
      const { error } = await supabase?.from('yards')?.delete()?.eq('id', yardId);

      if (!error) {
        if (selectedYardId === yardId) setSelectedYardId(null);
        await fetchYards();
      } else {
        alert(`Failed to delete yard: ${error?.message}`);
      }
    }
  };

  const handleBulkStatusChange = async (status) => {
    const isActive = status === 'active';
    const { error } = await supabase?.from('yards')?.update({ is_active: isActive })?.in('id', selectedYards);

    if (!error) {
      setSelectedYards([]);
      await fetchYards();
    }
  };

  const handleBulkCapacityUpdate = async () => {
    const newCapacity = prompt('Enter new capacity for selected yards:');
    if (newCapacity && !isNaN(newCapacity)) {
      const { error } = await supabase?.from('yards')?.update({ capacity: parseInt(newCapacity) })?.in('id', selectedYards);

      if (!error) {
        setSelectedYards([]);
        await fetchYards();
      }
    }
  };

  const handleBulkExport = () => {
    const headers = ['Name', 'Address', 'Region', 'Capacity', 'Utilization %', 'Manager', 'Status'];
    const rows = sortedYards
      ?.filter(y => selectedYards?.includes(y?.id))
      ?.map(yard => [
        yard?.name || '',
        yard?.address || '',
        yard?.region || '',
        yard?.capacity ?? '',
        yard?.utilization ?? '',
        yard?.manager || '',
        yard?.status || ''
      ]);
    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.map(cell => `"${String(cell)?.replace(/"/g, '""')}"`)?.join(','))
    ]?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `yards-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 's') {
        e?.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-background flex flex-col">
        <AdminHeader user={user} onLogout={handleLogout} />
        <AdminNavigation />

        <div className="flex-1 flex flex-col" style={{ marginTop: '7.5rem' }}>
          <div className="bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <Icon name="MapPin" size={24} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-foreground">Yards Management</h1>
                  <p className="text-sm text-muted-foreground">
                    {loading ? 'Loading...' : `${sortedYards?.length} ${sortedYards?.length === 1 ? 'yard' : 'yards'}`}
                    {!loading && yards?.some(y => y?.capacity) && ` • Total capacity: ${yards?.reduce((sum, yard) => sum + (yard?.capacity || 0), 0)} units`}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => {
                    const headers = ['Name', 'Address', 'Region', 'Capacity', 'Utilization %', 'Manager', 'Status'];
                    const rows = sortedYards?.map(yard => [
                      yard?.name || '',
                      yard?.address || '',
                      yard?.region || '',
                      yard?.capacity ?? '',
                      yard?.utilization ?? '',
                      yard?.manager || '',
                      yard?.status || ''
                    ]);
                    const csvContent = [
                      headers?.join(','),
                      ...rows?.map(row => row?.map(cell => `"${String(cell)?.replace(/"/g, '""')}"`)?.join(','))
                    ]?.join('\n');
                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `yards-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
                    document.body?.appendChild(link);
                    link?.click();
                    document.body?.removeChild(link);
                    URL.revokeObjectURL(url);
                  }}
                >
                  Export All
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleOpenAddModal}
                >
                  Add Yard
                </Button>
              </div>
            </div>
          </div>

          <BulkOperationsToolbar
            selectedCount={selectedYards?.length}
            onBulkStatusChange={handleBulkStatusChange}
            onBulkCapacityUpdate={handleBulkCapacityUpdate}
            onBulkExport={handleBulkExport}
            onClearSelection={() => setSelectedYards([])}
          />

          <div className="flex-1 flex overflow-hidden">
            <FilterSidebar
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
              isCollapsed={isFilterCollapsed}
              onToggleCollapse={() => setIsFilterCollapsed(!isFilterCollapsed)}
            />

            <div className="flex-1 flex flex-col overflow-hidden">
              {loading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex items-center space-x-3 text-muted-foreground">
                    <Icon name="Loader2" size={20} strokeWidth={2} className="animate-spin" />
                    <span className="text-sm">Loading yards...</span>
                  </div>
                </div>
              ) : (
                <YardsDataGrid
                  yards={sortedYards}
                  selectedYards={selectedYards}
                  onSelectYard={handleSelectYard}
                  onSelectAll={handleSelectAll}
                  onEditYard={handleOpenEditModal}
                  onDeleteYard={handleDeleteYard}
                  onSort={handleSort}
                  sortConfig={sortConfig}
                />
              )}

              <div className="bg-card border-t border-border px-4 py-3">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>Showing {sortedYards?.length} of {yards?.length} yards</span>
                  <div className="flex items-center space-x-4">
                    <button className="hover:text-foreground transition-hover">
                      <Icon name="ChevronLeft" size={16} strokeWidth={2} />
                    </button>
                    <span className="text-foreground font-medium">Page 1 of 1</span>
                    <button className="hover:text-foreground transition-hover">
                      <Icon name="ChevronRight" size={16} strokeWidth={2} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {selectedYardId && (
              <DetailPanel
                selectedYard={selectedYard}
                onClose={() => setSelectedYardId(null)}
              />
            )}
          </div>
        </div>

        <AddYardModal
          isOpen={isAddModalOpen}
          onClose={handleCloseModal}
          onSaveYard={handleSaveYard}
          editingYard={editingYard}
          saveError={modalSaveError}
        />
      </div>
    </ProtectedRoute>
  );
};

export default YardsManagementInterface;